/* eslint-disable complexity */
import { LOCATION_CHANGE } from 'connected-react-router';
import {
    CHANGE_METRIC_POSITION,
    CHANGE_SEGMENTATION,
} from '@domain/dashboard/metrics-report/metrics-report.state';
import { CHANGE_DASHBOARD_FILTER } from '@domain/dashboard/dashboard-filters/dashboard-filters.state';
import Routes from '@domain/router/routes.constants';
import { match } from '@shared/utils/match';
import { APP_LOADED, UPDATE_OBJECT_SELECTED } from '@domain/_redux/pareto-ads-app-redux/action/actionTypes';
import { createMiddleware } from '@shared/redux';
import { EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_SUCCEEDED } from '@domain/dashboard/campaigns/campaigns.state';
import { SAVE_HOT_SPOT_REQUEST_SUCCEEDED } from '@domain/dashboard/hotspot/hot-spot-container/hot-spot-container.state';
import { analytics } from '../../analytics';
import { getSharedData } from './utils';

const METRIC_POSITION_MAP = {
    firstMetric: 1,
    secondMetric: 2,
};

const trackView = (event: string) => async (state) => {
    const shared = getSharedData(state);
    if (!shared.clientId || !shared.accountId) {
        return;
    }

    await analytics.track(event, shared);
};

const routeChanges = {
    [Routes.CAMPAIGN_MANAGER]: trackView('dashboard_account_viewed'),
};

export const dashboardMiddleware = createMiddleware({
    [APP_LOADED]: match(state => state?.router?.location?.pathname, routeChanges),
    [LOCATION_CHANGE]: match((_, action) => action?.payload?.location?.pathname, routeChanges),
    [CHANGE_SEGMENTATION]: async (state, { segmentation }) => {
        const { metricsReport } = state?.dashboard || {};
        const { segmentation: previousSegmentation } = metricsReport || {};
        const shared = getSharedData(state);

        if (previousSegmentation === segmentation) {
            return;
        }

        await analytics.track('dashboard_time_group_changed', {
            ...shared,
            segmentation,
            previousSegmentation,
        });
    },
    [CHANGE_METRIC_POSITION]: async (state, { metric, metricPosition }) => {
        const { value: name, type } = metric;
        const { value: previousName } = state.dashboard.metricsReport[metricPosition].metric;
        const shared = getSharedData(state);

        if (!name || previousName === name) {
            return;
        }

        await analytics.track('dashboard_metric_changed', {
            ...shared,
            name,
            type,
            position: METRIC_POSITION_MAP[metricPosition] || 0,
        });
    },
    [CHANGE_DASHBOARD_FILTER]: async (state, action) => {
        const shared = getSharedData(state);
        const { ranges } = action.payload;

        if (!!ranges?.date?.endDate && !!ranges?.date?.startDate) {
            await analytics.track('dashboard_date_interval_changed', {
                ...shared,
                ranges,
            });
        }
    },
    [UPDATE_OBJECT_SELECTED]: async (state, { objectSelected }) => {
        const route = state?.router?.location?.pathname;
        if (route !== Routes.CAMPAIGN_MANAGER) {
            return;
        }

        const { company } = state?.data?.loggedUser;
        const { team_id: teamId } = company;
        const { data } = objectSelected;
        const { type } = data;

        if (type === 'account') {
            const { client_selected: clientSelected } = objectSelected;
            const { id: accountId } = data;
            const { id: clientId } = clientSelected;

            await analytics.track('dashboard_account_viewed', {
                accountId,
                clientId,
                teamId,
                level: type,
            });
        } else if (type === 'client') {
            const { id: clientId } = data;
            await analytics.track('dashboard_account_viewed', {
                clientId,
                teamId,
                level: type,
            });
        }
    },
    [EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_SUCCEEDED]: async (state, { requestAction }) => {
        const shared = getSharedData(state);
        const {
            accountId,
            campaignId,
            subLevelId,
            field,
            fieldValue,
            originalValue,
        } = requestAction.payload;
        const { data } = requestAction.config;

        const fields = {
            ...shared,
            accountId,
            campaignId,
            subLevelId,
        };

        if (field === 'budget') {
            await analytics.track('campaign_budget_changed', {
                ...fields,
                budget: parseFloat(fieldValue),
                previousBudget: parseFloat(originalValue),
            });
        } else if (field === 'status') {
            await analytics.track('campaign_status_changed', {
                ...fields,
                status: data.status.toLowerCase(),
                previousStatus: originalValue,
            });
        }
    },
    [SAVE_HOT_SPOT_REQUEST_SUCCEEDED]: async (state, { payload }) => {
        const shared = getSharedData(state);
        const { data } = payload;
        const {
            categories,
            title,
            description,
            start_date: startDate,
            end_date: endDate,
        } = data;

        await analytics.track('hot_spot_created', {
            ...shared,
            title,
            description,
            startDate: new Date(startDate).toJSON(),
            endDate: endDate ? new Date(endDate).toJSON() : undefined,
            categories: categories.map(it => it.name),
        });
    },
}, { beforeChanges: true });
